import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, alpha, useTheme, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';

const WA_GREEN = '#075E54';
const WA_BG = '#ECE5DD';
const WA_BUBBLE = '#FFFFFF';
const TEAL = '#00A76F';
const LINE = '#EBEBEB';

function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function substituteVariables(message, sampleValues) {
    if (!message) return '';
    // Replace \n with <br> for HTML rendering
    let html = escapeHtml(message);
    html = html.replace(/\n/g, '<br/>');
    // Highlight substituted variables
    html = html.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        if (sampleValues && sampleValues[varName] !== undefined) {
            const val = escapeHtml(sampleValues[varName]);
            return `<span style="background:rgba(0,167,111,0.12);color:#007B55;padding:1px 4px;border-radius:4px;font-weight:600;font-size:0.78em">${val}</span>`;
        }
        return `<span style="background:rgba(145,158,171,0.16);color:#637381;padding:1px 4px;border-radius:4px;font-size:0.78em">${match}</span>`;
    });
    return html;
}

export default function MessagePreview({
    label,
    message,
    onChange,
    variables,
    sampleValues,
    practiceName,
    practiceLogo,
}) {
    const [editing, setEditing] = React.useState(false);
    const [editValue, setEditValue] = React.useState(message || '');
    const textareaRef = useRef(null);
    const theme = useTheme();

    const displayMessage = substituteVariables(message, sampleValues);

    const handleOpen = () => {
        setEditValue(message || '');
        setEditing(true);
    };

    const handleSave = () => {
        onChange(editValue);
        setEditing(false);
    };

    const handleInsertVariable = (varName) => {
        const textarea = textareaRef?.current;
        const insertText = varName;

        if (textarea) {
            const start = textarea.selectionStart || 0;
            const end = textarea.selectionEnd || 0;
            const newValue = editValue.substring(0, start) + insertText + editValue.substring(end);
            setEditValue(newValue);
            setTimeout(() => {
                const newPos = start + insertText.length;
                textarea.selectionStart = newPos;
                textarea.selectionEnd = newPos;
                textarea.focus();
            }, 0);
        } else {
            setEditValue(editValue + insertText);
        }
    };

    return (
        <Box sx={{ mt: 1.5 }}>
            {/* Section label */}
            <Typography sx={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#B0B0B0',
                mb: 1,
            }}>
                {label || 'Message preview'}
            </Typography>

            {/* WhatsApp phone mockup */}
            <Box sx={{
                width: '100%',
                borderRadius: 2.5,
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
                '&:hover .edit-overlay': { opacity: 1 },
            }}
                onClick={handleOpen}
            >
                {/* WhatsApp header bar */}
                <Box sx={{
                    bgcolor: WA_GREEN,
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                }}>
                    {practiceLogo ? (
                        <Box
                            component="img"
                            src={practiceLogo}
                            alt={practiceName || 'Practice'}
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <Box sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.common.white, 0.2),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Icon icon="mdi:robot-outline" width={16} color={theme.palette.common.white} />
                        </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ color: theme.palette.common.white, fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.2 }}>
                            {practiceName || 'Your Practice'}
                        </Typography>
                        <Typography sx={{ color: alpha(theme.palette.common.white, 0.7), fontSize: '0.62rem', lineHeight: 1.2 }}>
                            Auto-reply
                        </Typography>
                    </Box>
                    <Icon icon="mdi:pencil" width={16} color={alpha(theme.palette.common.white, 0.7)} />
                </Box>

                {/* Chat area */}
                <Box sx={{
                    bgcolor: WA_BG,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    p: 1.5,
                    flex: 1,
                }}>
                    {/* Message bubble */}
                    <Box sx={{
                        bgcolor: WA_BUBBLE,
                        borderRadius: '0 12px 12px 12px',
                        p: 1.5,
                        maxWidth: '92%',
                        position: 'relative',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: -8,
                            width: 0,
                            height: 0,
                            borderTop: `8px solid ${WA_BUBBLE}`,
                            borderLeft: '8px solid transparent',
                        },
                    }}>
                        {/* Safe: content is escaped via escapeHtml before variable substitution */}
                        {/* eslint-disable-next-line react/no-danger */}
                        <Typography
                            sx={{
                                fontSize: '0.82rem',
                                lineHeight: 1.6,
                                wordBreak: 'break-word',
                                color: theme.palette.grey[800],
                            }}
                            dangerouslySetInnerHTML={{ __html: displayMessage }}
                        />
                        <Typography sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            mt: 0.5,
                            color: '#8696A0',
                            fontSize: '0.6rem',
                        }}>
                            Just now
                            <Icon icon="mdi:check-all" width={14} color="#53BDEB" />
                        </Typography>
                    </Box>
                </Box>

                {/* Hover overlay with edit hint */}
                <Box className="edit-overlay" sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    borderRadius: 3,
                }}>
                    <Icon icon="mdi:pencil" width={20} color="#fff" />
                    <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>
                        Tap to edit message
                    </Typography>
                </Box>
            </Box>

            {/* Edit Dialog */}
            <Dialog
                open={editing}
                onClose={() => setEditing(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px' },
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#212B36' }}>
                        Edit Message
                    </Typography>
                    <IconButton onClick={() => setEditing(false)} size="small">
                        <Icon icon="mdi:close" width={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        inputRef={textareaRef}
                        multiline
                        minRows={4}
                        maxRows={10}
                        fullWidth
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        sx={{
                            mt: 1,
                            '& .MuiOutlinedInput-root': {
                                fontSize: '14px',
                                color: '#3D3D3D',
                                borderRadius: '12px',
                                lineHeight: 1.6,
                                '& fieldset': {
                                    borderWidth: '1.5px',
                                    borderColor: LINE,
                                },
                                '&:hover fieldset': {
                                    borderColor: '#ccc',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: TEAL,
                                    borderWidth: '1.5px',
                                },
                            },
                        }}
                    />
                    {variables?.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                                Insert variable
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {variables.map((v) => (
                                    <Box
                                        key={v}
                                        component="button"
                                        onClick={() => handleInsertVariable(v)}
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            p: '4px 10px',
                                            borderRadius: '8px',
                                            background: '#F4F6F8',
                                            color: '#637381',
                                            border: `1px solid ${LINE}`,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            transition: 'all 0.15s',
                                            '&:hover': {
                                                background: 'rgba(0,167,111,0.08)',
                                                color: TEAL,
                                                borderColor: TEAL,
                                            },
                                        }}
                                    >
                                        {v}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                    <Box
                        component="button"
                        onClick={() => setEditing(false)}
                        sx={{
                            px: 2.5, py: 1,
                            borderRadius: '10px',
                            border: '1.5px solid #EBEBEB',
                            background: '#fff',
                            cursor: 'pointer',
                            fontSize: 13, fontWeight: 600,
                            color: '#637381',
                            fontFamily: 'inherit',
                            '&:hover': { borderColor: '#ccc', background: '#FAFAFA' },
                        }}
                    >
                        Cancel
                    </Box>
                    <Box
                        component="button"
                        onClick={handleSave}
                        sx={{
                            px: 2.5, py: 1,
                            borderRadius: '10px',
                            border: 'none',
                            background: TEAL,
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: 13, fontWeight: 600,
                            fontFamily: 'inherit',
                            '&:hover': { background: '#007B55' },
                        }}
                    >
                        Save Changes
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

MessagePreview.propTypes = {
    label: PropTypes.string,
    message: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    variables: PropTypes.arrayOf(PropTypes.string),
    sampleValues: PropTypes.object,
    practiceName: PropTypes.string,
    practiceLogo: PropTypes.string,
};

MessagePreview.defaultProps = {
    label: 'Message preview',
    message: '',
    variables: [],
    sampleValues: {},
    practiceName: '',
    practiceLogo: '',
};
